(ns manage.poi.locations.ocps
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Link$default" :as Link]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.string :as str]
            [common.db.effects.csv :as csv]
            [common.form :as form]
            [common.modal.dialog :refer [deletion-confirmation]]
            [common.modal.view :refer [defmodal]]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.poi :as poi.q]
            [common.widgets.upload :as upload]
            [manage.poi.locations.form :refer [build-location-payload empty-location-defaults
                                               location-default-values location-form-fields]]
            [re-frame.core :as rf]
            [uix.core :refer [$] :as uix]
            [uix.re-frame :refer [use-subscribe]])
  (:require-macros [common.modal.view]
                   [common.ocp.view]))

(defocp create-location-ocp :poi/create-location {:width "35%"} [_props]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {dataset-ready :isSuccess} (use-query (poi.q/ensure-dataset {:advertiser-id advertiser-id}))
        {available-labels :data} (use-query (poi.q/label-options-query {:advertiser-id advertiser-id
                                                                        :enabled? dataset-ready}))
        {:keys [control handleSubmit watch]} (form/use-form #js {:defaultValues empty-location-defaults})
        {create-location :mutate isPending :isPending} (use-mutation (poi.q/create-location-mutation {:advertiser-id advertiser-id}))
        on-submit (fn [^js data]
                    (create-location (build-location-payload data)
                                     #js {:onSuccess (fn [_] (rf/dispatch [:ocp/close]))}))]
    ($ ocp {:title "New Location"
            :on-submit (handleSubmit on-submit)
            :footer ($ Button {:type "submit" :variant "contained" :fullWidth true
                               :disabled isPending}
                       "Create Location")}
       (location-form-fields {:control control :watch watch
                              :available-labels available-labels
                              :disable-external-id? false}))))

(defocp location-ocp :poi/location {:width "35%"} [{:keys [location]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {dataset-ready :isSuccess} (use-query (poi.q/ensure-dataset {:advertiser-id advertiser-id}))
        {available-labels :data} (use-query (poi.q/label-options-query {:advertiser-id advertiser-id
                                                                        :enabled? dataset-ready}))
        {:keys [control handleSubmit watch]} (form/use-form #js {:defaultValues (location-default-values location)})
        {update-location :mutate isPending :isPending} (use-mutation (poi.q/update-location-mutation {:advertiser-id advertiser-id}))
        on-submit (fn [^js data]
                    (update-location #js {:location-id (:id location)
                                          :payload (build-location-payload data)}
                                     #js {:onSuccess (fn [_] (rf/dispatch [:ocp/close]))}))]
    ($ ocp {:title (str "Location: " (:external-id location))
            :on-submit (handleSubmit on-submit)
            :footer ($ Button {:type "submit" :variant "contained"
                               :disabled isPending}
                       "Save")}
       (location-form-fields {:control control :watch watch
                              :available-labels available-labels
                              :disable-external-id? true}))))

(def ^:private example-csv
  (str "external-id,lat,lon,name,address,city,postcode,phone,labels,always-open,always-closed,timezone,monday,tuesday,wednesday,thursday,friday,saturday,sunday\r\n"
       "STORE-001,52.3676,4.9041,Amsterdam Store,Damrak 1,Amsterdam,1012 LG,+31201234567,flagship retail,,,Europe/Amsterdam,09:00-21:00,09:00-21:00,09:00-21:00,09:00-21:00,09:00-21:00,10:00-20:00,12:00-18:00\r\n"
       "STORE-002,51.9225,4.4792,Rotterdam Store,Coolsingel 10,Rotterdam,3011 AD,,retail,,,,,,,,,,,\r\n"
       "STORE-003,48.8566,2.3522,Paris Store,Rue de Rivoli 1,Paris,75001,,landmark,true,,,,,,,,,,\r\n"
       "STORE-004,50.8503,4.3517,Brussels Store,Grand Place 1,Brussels,1000,,seasonal,,true,,,,,,,,,"))

(defocp upload-csv-ocp :poi/upload-csv {:width "35%"} [_props]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        [file set-file] (uix/use-state nil)
        {:keys [mutate isPending error reset]} (use-mutation
                                                (poi.q/bulk-replace-locations-mutation
                                                 {:advertiser-id advertiser-id}))
        error-details (uix/use-memo
                       (fn []
                         (when error
                           (let [^js err-obj (some-> ^js (:data error) .-error)
                                 message (or (some-> err-obj .-message)
                                             (some-> ^js (:data error) .-message)
                                             "Failed to replace locations")
                                 details (some-> err-obj .-details js->clj)]
                             {:message message :details details})))
                       [error])
        on-submit (uix/use-callback
                   (fn []
                     (when file
                       (let [form-data (js/FormData.)]
                         (.append form-data "file" file)
                         (mutate form-data
                                 #js {:onSuccess (fn [_] (rf/dispatch [:ocp/close]))}))))
                   [file mutate])
        on-change (uix/use-callback
                   (fn [f]
                     (reset)
                     (set-file f))
                   [reset])]
    ($ ocp {:title "Upload CSV"
            :on-submit on-submit
            :footer ($ Button {:type "submit" :variant "contained" :fullWidth true
                               :disabled (or isPending (nil? file))}
                       (if isPending "Uploading..." "Replace Locations"))}
       ($ Alert {:severity "info" :sx #js {:mb 2}}
          ($ Typography {:variant "body2" :fontWeight "bold" :sx #js {:mb 0.5}} "CSV columns:")
          ($ :table {:style {:fontSize "13px" :borderCollapse "collapse"}}
             ($ :tbody
                (for [[col req desc] [["external-id" true "Unique location identifier"]
                                      ["lat" true "Latitude"]
                                      ["lon" true "Longitude"]
                                      ["name" false "Location name"]
                                      ["address" false "Street address"]
                                      ["city" false "City"]
                                      ["postcode" false "Postal code"]
                                      ["phone" false "Phone number"]
                                      ["labels" false "Space-separated labels"]
                                      ["always-open" false "true for 24/7 open"]
                                      ["always-closed" false "true for 24/7 closed"]
                                      ["timezone" false "Required with opening hours"]
                                      ["monday" false "Opening hours, e.g. 09:00-17:00"]
                                      ["tuesday" false ""]
                                      ["wednesday" false ""]
                                      ["thursday" false ""]
                                      ["friday" false ""]
                                      ["saturday" false ""]
                                      ["sunday" false ""]]]
                  ($ :tr {:key col}
                     ($ :td {:style {:fontFamily "monospace" :paddingRight "12px" :whiteSpace "nowrap"}} col)
                     ($ :td {:style {:paddingRight "12px" :color (if req "#1565c0" "#999")}}
                        (if req "required" "optional"))
                     ($ :td {:style {:color "#666"}} desc)))))
          ($ Link {:component "button"
                   :variant "body2"
                   :sx #js {:cursor "pointer" :verticalAlign "baseline" :mt 0.5}
                   :onClick (fn [] (csv/download-csv example-csv "locations-example"))}
             "Download example CSV"))
       ($ upload/upload
          {:enablePreview false
           :enableEdit false
           :allow-macro? false
           :filetype "csv"
           :extensions ".csv"
           :onChange on-change})
       (when file
         ($ Stack {:direction "row" :alignItems "center" :spacing 1 :sx #js {:mt 1}}
            ($ Typography {:variant "body2" :color "text.secondary"}
               (str (.-name file) " (" (Math/round (/ (.-size file) 1024)) " KB)"))
            ($ Button {:size "small"
                       :onClick (fn [] (reset) (set-file nil))
                       :sx #js {:minWidth "auto" :textTransform "none"}}
               "Clear")))
       (when error-details
         ($ Alert {:severity "error" :sx #js {:mt 2}}
            ($ Typography {:variant "body2" :fontWeight "bold"} (:message error-details))
            (when-let [details (:details error-details)]
              (let [format-msg (fn [msg]
                                 (cond
                                   (vector? msg) (str/join ", " msg)
                                   (map? msg) (let [by-val (reduce (fn [acc [k vs]]
                                                                     (let [v (str/join ", " vs)]
                                                                       (update acc v (fnil conj []) k)))
                                                                   {} msg)]
                                                (->> by-val
                                                     (map (fn [[v ks]] (str v ": " (str/join ", " ks))))
                                                     (str/join "; ")))
                                   :else (str msg)))]
                ($ :ul {:style {:maxHeight "200px" :overflow "auto" :margin "4px 0 0" :paddingLeft "20px"}}
                   (for [detail details
                         :let [line (get detail "line")]]
                     ($ :li {:key line}
                        (str "Line " line ": " (format-msg (get detail "message")))))))))))))

(defmodal confirm-delete-location :poi/confirm-delete-location
  [{:keys [location-id location-name]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {delete-location :mutate} (use-mutation (poi.q/delete-location-mutation {:advertiser-id advertiser-id}))]
    (deletion-confirmation
     {:entity-name "Location"
      :item-label (or location-name (str location-id))
      :on-submit #(delete-location location-id
                                   #js {:onSuccess (fn [_] (rf/dispatch [:ocp/close]))})})))

(defmodal confirm-delete-all-locations :poi/confirm-delete-all-locations
  [_props]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {bulk-delete :mutate} (use-mutation (poi.q/bulk-delete-locations-mutation {:advertiser-id advertiser-id}))]
    (deletion-confirmation
     {:entity-name "all locations"
      :confirmation-message "This will permanently delete all locations. Are you sure?"
      :on-submit #(bulk-delete nil)})))
